import type { ClientMessage, ServerEvent } from '@harness/shared/protocol';

/** The part of WebSocket the chat uses; tests pass a fake. */
export interface SocketLike {
  readonly readyState: number;
  send(data: string): void;
  close(): void;
  onopen: (() => void) | null;
  onclose: (() => void) | null;
  onmessage: ((event: { data: unknown }) => void) | null;
}

export interface ChatSocket {
  /** False if the socket is not connected; the message is dropped. */
  send(message: ClientMessage): boolean;
  close(): void;
}

export interface ChatSocketOptions {
  url: string;
  onEvent: (event: ServerEvent) => void;
  onConnection: (connected: boolean) => void;
  create?: (url: string) => SocketLike;
  delays?: readonly number[];
  setTimer?: (run: () => void, ms: number) => void;
}

const OPEN = 1;
export const RECONNECT_DELAYS = [1000, 2000, 5000, 10000] as const;

export function openChatSocket(options: ChatSocketOptions): ChatSocket {
  const create = options.create ?? ((url: string): SocketLike => new WebSocket(url) as unknown as SocketLike);
  const delays = options.delays ?? RECONNECT_DELAYS;
  const setTimer = options.setTimer ?? ((run: () => void, ms: number): void => void setTimeout(run, ms));
  let socket: SocketLike | null = null;
  let attempts = 0;
  let closed = false;

  const connect = (): void => {
    const current = create(options.url);
    socket = current;
    current.onopen = (): void => {
      attempts = 0;
      options.onConnection(true);
    };
    current.onmessage = (event): void => {
      if (typeof event.data !== 'string') return;
      try {
        options.onEvent(JSON.parse(event.data) as ServerEvent);
      } catch {
        // Ignore frames that are not JSON.
      }
    };
    current.onclose = (): void => {
      if (socket !== current) return;
      socket = null;
      options.onConnection(false);
      if (closed) return;
      const delay = delays[Math.min(attempts, delays.length - 1)] ?? 10000;
      attempts += 1;
      setTimer(() => {
        if (!closed) connect();
      }, delay);
    };
  };

  connect();
  return {
    send(message): boolean {
      if (socket === null || socket.readyState !== OPEN) return false;
      socket.send(JSON.stringify(message));
      return true;
    },
    close(): void {
      closed = true;
      socket?.close();
    },
  };
}
